import { DocumentStatus } from "@/types";

/**
 * Document status groupings for easier status checking throughout the application
 */

export const PROCESSING_STATUSES: DocumentStatus[] = [DocumentStatus.PROCESSING, DocumentStatus.LOADING];

export const FAILED_STATUSES: DocumentStatus[] = [
  DocumentStatus.PROCESSING_FAILED,
  DocumentStatus.LOADING_FAILED,
  DocumentStatus.FAILED,
  DocumentStatus.UPLOAD_FAILED,
];

export const COMPLETED_STATUSES: DocumentStatus[] = [DocumentStatus.COMPLETED, DocumentStatus.ARCHIVED];

export const ACTIVE_STATUSES: DocumentStatus[] = [DocumentStatus.UPLOADED, DocumentStatus.PROCESSING];

/**
 * Helper functions for status checks
 */

export const isProcessingStatus = (status: DocumentStatus | undefined): boolean => {
  return status !== undefined && PROCESSING_STATUSES.includes(status);
};

export const isFailedStatus = (status: DocumentStatus | undefined): boolean => {
  return status !== undefined && FAILED_STATUSES.includes(status);
};

export const isCompletedStatus = (status: DocumentStatus | undefined): boolean => {
  return status !== undefined && COMPLETED_STATUSES.includes(status);
};

export const isActiveStatus = (status: DocumentStatus | undefined): boolean => {
  return status !== undefined && ACTIVE_STATUSES.includes(status);
};

/**
 * Statuses where the backend will NOT transition the document further.
 * User action or an external event is required to move out of these states.
 */
export const TERMINAL_STATUSES: DocumentStatus[] = [
  // Success terminals
  DocumentStatus.COMPLETED,
  DocumentStatus.ARCHIVED,
  DocumentStatus.DELETED,
  // Failure terminals
  DocumentStatus.PROCESSING_FAILED,
  DocumentStatus.LOADING_FAILED,
  DocumentStatus.FAILED,
  DocumentStatus.UPLOAD_FAILED,
  // User-action terminals
  DocumentStatus.REJECTED,
  DocumentStatus.REVIEW_REQUIRED,
];

/**
 * Returns true if the document is in a state where the backend may still
 * transition it. This is the inverse of terminal — any status NOT in
 * TERMINAL_STATUSES is considered in-flight and should be polled.
 *
 * In-flight statuses: UPLOADED, PROCESSING, REPROCESS, APPROVED, LOADING, LOADED
 */
export const isInFlightStatus = (status: DocumentStatus | undefined): boolean => {
  return status !== undefined && !TERMINAL_STATUSES.includes(status);
};

/**
 * Returns true if the document is in an actively-processing state
 * (as opposed to a queued/waiting in-flight state). Used for tiered polling intervals.
 */
export const isActiveProcessingStatus = (status: DocumentStatus | undefined): boolean => {
  return status !== undefined && PROCESSING_STATUSES.includes(status);
};
